/**
 * operationsConfig.js
 *
 * Central configuration for the FleetSkipper Operations module.
 * Contains all constants, enums, and configuration for:
 * - Voyage management
 * - Operational checklists
 * - Main logbook
 *
 * FleetSkipper - Built for Mariners.
 */

// ============================================================================
// VOYAGE MANAGEMENT
// ============================================================================

/**
 * Voyage lifecycle statuses
 */
export const VOYAGE_STATUS = {
  DRAFT: 'draft',
  PRE_DEPARTURE: 'pre_departure',
  UNDERWAY: 'underway',
  ARRIVAL: 'arrival',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  ABORTED: 'aborted'
};

/**
 * Human-readable labels for voyage statuses
 */
export const VOYAGE_STATUS_LABELS = {
  [VOYAGE_STATUS.DRAFT]: 'Draft',
  [VOYAGE_STATUS.PRE_DEPARTURE]: 'Pre-Departure',
  [VOYAGE_STATUS.UNDERWAY]: 'Underway',
  [VOYAGE_STATUS.ARRIVAL]: 'Arrival',
  [VOYAGE_STATUS.COMPLETED]: 'Completed',
  [VOYAGE_STATUS.CANCELLED]: 'Cancelled',
  [VOYAGE_STATUS.ABORTED]: 'Aborted'
};

/**
 * Log entry types for voyage log
 */
export const LOG_ENTRY_TYPES = {
  POSITION: 'position',
  WEATHER: 'weather',
  COMMS: 'comms',
  NOTE: 'note',
  OPERATION_START: 'operation_start',
  OPERATION_END: 'operation_end',
  LET_GO: 'let_go',
  ALL_FAST: 'all_fast',
  PRE_DEPARTURE_COMPLETE: 'pre_departure_complete',
  DRILL: 'drill',
  DEFECT: 'defect',
  EMERGENCY: 'emergency'
};

/**
 * Human-readable labels for log entry types
 */
export const LOG_ENTRY_TYPE_LABELS = {
  [LOG_ENTRY_TYPES.POSITION]: 'Position',
  [LOG_ENTRY_TYPES.WEATHER]: 'Weather',
  [LOG_ENTRY_TYPES.COMMS]: 'Communication',
  [LOG_ENTRY_TYPES.NOTE]: 'Note',
  [LOG_ENTRY_TYPES.OPERATION_START]: 'Operation Started',
  [LOG_ENTRY_TYPES.OPERATION_END]: 'Operation Ended',
  [LOG_ENTRY_TYPES.LET_GO]: 'Let Go',
  [LOG_ENTRY_TYPES.ALL_FAST]: 'All Fast',
  [LOG_ENTRY_TYPES.PRE_DEPARTURE_COMPLETE]: 'Pre-Departure Complete',
  [LOG_ENTRY_TYPES.DRILL]: 'Drill',
  [LOG_ENTRY_TYPES.DEFECT]: 'Defect',
  [LOG_ENTRY_TYPES.EMERGENCY]: 'Emergency'
};

/**
 * Icons for log entry types (Lucide icon names)
 */
export const LOG_ENTRY_TYPE_ICONS = {
  [LOG_ENTRY_TYPES.POSITION]: 'MapPin',
  [LOG_ENTRY_TYPES.WEATHER]: 'Cloud',
  [LOG_ENTRY_TYPES.COMMS]: 'Radio',
  [LOG_ENTRY_TYPES.NOTE]: 'FileText',
  [LOG_ENTRY_TYPES.OPERATION_START]: 'Play',
  [LOG_ENTRY_TYPES.OPERATION_END]: 'CheckCircle',
  [LOG_ENTRY_TYPES.LET_GO]: 'Anchor',
  [LOG_ENTRY_TYPES.ALL_FAST]: 'Anchor',
  [LOG_ENTRY_TYPES.PRE_DEPARTURE_COMPLETE]: 'CheckSquare',
  [LOG_ENTRY_TYPES.DRILL]: 'AlertTriangle',
  [LOG_ENTRY_TYPES.DEFECT]: 'AlertCircle',
  [LOG_ENTRY_TYPES.EMERGENCY]: 'Siren'
};

/**
 * Operation types (inline operations during voyage)
 */
export const OPERATION_TYPES = {
  CREW_TRANSFER: 'crew_transfer',
  PILOT_TRANSFER: 'pilot_transfer',
  STANDBY: 'standby',
  TOWING: 'towing',
  DIVING_SUPPORT: 'diving_support',
  ANCHORING: 'anchoring',
  RESTRICTED_VISIBILITY: 'restricted_visibility',
  PRE_ARRIVAL: 'pre_arrival'
};

/**
 * Human-readable labels for operation types
 */
export const OPERATION_TYPE_LABELS = {
  [OPERATION_TYPES.CREW_TRANSFER]: 'Crew Transfer',
  [OPERATION_TYPES.PILOT_TRANSFER]: 'Pilot Transfer',
  [OPERATION_TYPES.STANDBY]: 'Standby',
  [OPERATION_TYPES.TOWING]: 'Towing',
  [OPERATION_TYPES.DIVING_SUPPORT]: 'Diving Support',
  [OPERATION_TYPES.ANCHORING]: 'Anchoring',
  [OPERATION_TYPES.RESTRICTED_VISIBILITY]: 'Restricted Visibility',
  [OPERATION_TYPES.PRE_ARRIVAL]: 'Pre-Arrival Checks',
  'pre_departure': 'Pre-Departure',
  'arrival': 'Arrival'
};

/**
 * Default operations list - used to seed new installations
 * Each has id and label format for consistency
 */
export const DEFAULT_OPERATIONS_LIST = [
  { id: 'crew_transfer', label: 'Crew Transfer', enabled: true },
  { id: 'pilot_transfer', label: 'Pilot Transfer', enabled: true },
  { id: 'standby', label: 'Standby', enabled: true },
  { id: 'towing', label: 'Towing', enabled: true },
  { id: 'diving_support', label: 'Diving Support', enabled: true },
  { id: 'anchoring', label: 'Anchoring', enabled: true },
  { id: 'restricted_visibility', label: 'Restricted Visibility', enabled: true },
  // FT-80: Daily Checks in mid-voyage Log picker (disabled by default - most vessels do checks pre-departure)
  { id: 'daily_checks', label: 'Daily Checks (Mid-Voyage)', enabled: false, permanent: true }
  // pre_arrival removed: it belongs to DEFAULT_PHASE_OPERATIONS only — having it here caused FT-219 duplicates
];

/**
 * Get operation type labels from voyage settings
 * If operationsList exists in settings, use that (user-controlled)
 * Otherwise fall back to legacy customOperations pattern or OPERATION_TYPE_LABELS
 * @param {Object} voyageSettings - Voyage settings object from storage
 * @returns {Object} Operation labels keyed by id
 */
export const getOperationTypeLabels = (voyageSettings) => {
  // New unified list approach - operationsList is source of truth
  if (voyageSettings?.operationsList && voyageSettings.operationsList.length > 0) {
    const labels = {};
    voyageSettings.operationsList
      .filter(op => op.enabled !== false) // Only include enabled operations
      .forEach(op => {
        labels[op.id] = op.label;
      });
    // Always include pre_departure and arrival (system operations)
    labels['pre_departure'] = 'Pre-Departure';
    labels['arrival'] = 'Arrival';
    return labels;
  }

  // Legacy fallback: merge OPERATION_TYPE_LABELS with customOperations
  const labels = { ...OPERATION_TYPE_LABELS };
  (voyageSettings?.customOperations || []).forEach(op => {
    labels[op.id] = op.label;
  });
  return labels;
};

/**
 * Default voyage purposes list - used to seed new installations
 */
export const DEFAULT_PURPOSES_LIST = [
  { id: 'commercial', label: 'Commercial Operations', enabled: true },
  { id: 'training', label: 'Training', enabled: true },
  { id: 'maintenance', label: 'Maintenance/Trials', enabled: true },
  { id: 'transit', label: 'Transit', enabled: true },
  { id: 'emergency', label: 'Emergency Response', enabled: true }
];

/**
 * Get voyage purpose labels from voyage settings
 * If purposesList exists in settings, use that (user-controlled)
 * Otherwise fall back to legacy pattern or defaults
 * @param {Object} voyageSettings - Voyage settings object from storage
 * @returns {Object} Purpose labels keyed by id
 */
export const getVoyagePurposeLabels = (voyageSettings) => {
  // New unified list approach - purposesList is source of truth
  if (voyageSettings?.purposesList && voyageSettings.purposesList.length > 0) {
    const labels = {};
    voyageSettings.purposesList
      .filter(p => p.enabled !== false) // Only include enabled purposes
      .forEach(p => {
        labels[p.id] = p.label;
      });
    return labels;
  }

  // Legacy fallback: use VOYAGE_PURPOSE_LABELS with customPurposes
  const labels = { ...VOYAGE_PURPOSE_LABELS };
  (voyageSettings?.customPurposes || []).forEach(p => {
    // Custom purposes were stored as strings, not objects
    const id = `custom_${p.toLowerCase().replace(/\s+/g, '_')}`;
    labels[id] = p;
  });
  return labels;
};

/**
 * Icons for operation types (Lucide icon names)
 */
export const OPERATION_TYPE_ICONS = {
  [OPERATION_TYPES.CREW_TRANSFER]: 'Users',
  [OPERATION_TYPES.PILOT_TRANSFER]: 'UserCheck',
  [OPERATION_TYPES.STANDBY]: 'Clock',
  [OPERATION_TYPES.TOWING]: 'Link',
  [OPERATION_TYPES.DIVING_SUPPORT]: 'Waves',
  [OPERATION_TYPES.ANCHORING]: 'Anchor',
  [OPERATION_TYPES.RESTRICTED_VISIBILITY]: 'Eye'
};

// ============================================================================
// OPERATION-SPECIFIC CHECKLISTS - SIMPLIFIED VERIFICATION LISTS
// ============================================================================
// Philosophy: These are VERIFICATION checklists for qualified skippers.
// Users are encouraged to customise for their vessel/company.
// Equipment inspections are handled by the Maintenance module.

/**
 * Crew Transfer (Personnel Transfer) - Simplified
 * Regulatory: WBC3 25.5-25.6
 */
export const CREW_TRANSFER_CHECKLIST = {
  id: 'crew_transfer',
  name: 'Personnel Transfer',
  description: 'Before transferring persons',
  icon: 'Users',
  regulatory: 'WBC3 25.5-25.6',
  isCustomisable: true,
  fields: [
    { id: 'structure', label: 'Structure/Platform', type: 'text', required: true, placeholder: 'e.g., WTG Alpha-01' },
    { id: 'embarking', label: 'Personnel Embarking', type: 'number', required: true, placeholder: '0' },
    { id: 'disembarking', label: 'Personnel Disembarking', type: 'number', required: true, placeholder: '0' }
  ],
  checks: [
    { id: 'pt_1', text: 'Weather and sea state within transfer limits', required: true },
    { id: 'pt_2', text: 'MOB recovery readiness confirmed', required: true },
    { id: 'pt_3', text: 'Transfer equipment rigged and secure', required: true },
    { id: 'pt_4', text: 'All transferring personnel wearing lifejackets', required: true },
    { id: 'pt_5', text: 'Communications established with receiving party', required: true },
    { id: 'pt_6', text: 'Crew briefed and lookout posted', required: true }
  ]
};

/**
 * Pilot Transfer - Simplified
 * Regulatory: WBC3 27, IMO A.1045(27)
 */
export const PILOT_TRANSFER_CHECKLIST = {
  id: 'pilot_transfer',
  name: 'Pilot Transfer',
  description: 'Before pilot transfer',
  icon: 'UserCheck',
  regulatory: 'WBC3 27, IMO A.1045(27)',
  isCustomisable: true,
  fields: [
    { id: 'vessel_name', label: 'Target Vessel', type: 'text', required: true, placeholder: 'e.g., MV Example' },
    { id: 'pilot_name', label: 'Pilot Name', type: 'text', required: true },
    {
      id: 'direction', label: 'Direction', type: 'radio', required: true,
      options: [
        { value: 'embarking', label: 'Embarking' },
        { value: 'disembarking', label: 'Disembarking' }
      ]
    }
  ],
  checks: [
    { id: 'pt_1', text: 'Weather and sea state within transfer limits', required: true },
    { id: 'pt_2', text: 'MOB recovery readiness confirmed', required: true },
    { id: 'pt_3', text: 'Pilot ladder confirmed rigged correctly', required: true },
    { id: 'pt_4', text: 'All transferring personnel wearing lifejackets', required: true },
    { id: 'pt_5', text: 'Communications established with target vessel', required: true },
    { id: 'pt_6', text: 'Crew briefed and lookout posted', required: true }
  ]
};

/**
 * Towing - Simplified
 * Regulatory: WBC3 Ch 26
 */
export const TOWING_CHECKLIST = {
  id: 'towing',
  name: 'Towing Operations',
  description: 'Before starting a tow',
  icon: 'Link',
  regulatory: 'WBC3 Ch 26',
  isCustomisable: true,
  fields: [
    { id: 'tow_name', label: 'Tow Name/Description', type: 'text', required: true, placeholder: 'e.g., Barge "Titan"' },
    {
      id: 'towing_method', label: 'Towing Method', type: 'select', required: true,
      options: [
        { value: 'towline', label: 'Towline' },
        { value: 'alongside', label: 'Alongside' },
        { value: 'pushing', label: 'Pushing' }
      ]
    }
  ],
  checks: [
    { id: 'tow_1', text: 'Tow assessed and suitable for conditions', required: true },
    { id: 'tow_2', text: 'Risk assessment completed', required: true },
    { id: 'tow_3', text: 'Towing gear inspected including emergency release', required: true },
    { id: 'tow_4', text: 'Communications established with tow', required: true },
    { id: 'tow_5', text: 'Crew briefed and at stations', required: true }
  ]
};

/**
 * Anchoring - Simplified
 * Regulatory: WBC3 20
 */
export const ANCHORING_CHECKLIST = {
  id: 'anchoring',
  name: 'Anchoring',
  description: 'Before anchoring',
  icon: 'Anchor',
  regulatory: 'WBC3 20',
  isCustomisable: true,
  fields: [
    { id: 'depth', label: 'Depth (m)', type: 'number', required: true, placeholder: '0' },
    { id: 'cable_out', label: 'Cable Out (m)', type: 'number', required: true, placeholder: '0' }
  ],
  checks: [
    { id: 'anch_1', text: 'Position suitable - clear of hazards and traffic', required: true },
    { id: 'anch_2', text: 'Depth and bottom type checked', required: true },
    { id: 'anch_3', text: 'Swinging room adequate', required: true },
    { id: 'anch_4', text: 'Anchor ready to let go', required: true },
    { id: 'anch_5', text: 'Anchor watch arrangements confirmed', required: false }
  ]
};

/**
 * Standby - Simplified
 * Regulatory: WBC3 3.14
 */
export const STANDBY_CHECKLIST = {
  id: 'standby',
  name: 'Standby',
  description: 'When on standby',
  icon: 'Clock',
  regulatory: 'WBC3 3.14',
  isCustomisable: true,
  fields: [
    { id: 'client', label: 'Client/Control', type: 'text', required: true, placeholder: 'e.g., Wind Farm Control' },
    { id: 'purpose', label: 'Standby Purpose', type: 'text', required: false, placeholder: 'e.g., Awaiting personnel' }
  ],
  checks: [
    { id: 'sb_1', text: 'Standby position confirmed', required: true },
    { id: 'sb_2', text: 'Communications established with control', required: true },
    { id: 'sb_3', text: 'Crew aware of standby duties', required: true }
  ]
};

/**
 * Diving Support - Simplified
 * Regulatory: WBC3 25.3, HSE ACOP L104
 */
export const DIVING_SUPPORT_CHECKLIST = {
  id: 'diving_support',
  name: 'Diving Support',
  description: 'Before supporting dive operations',
  icon: 'Waves',
  regulatory: 'WBC3 25.3, HSE ACOP L104',
  isCustomisable: true,
  fields: [
    { id: 'num_divers', label: 'Number of Divers', type: 'number', required: true, placeholder: '0' },
    { id: 'dive_purpose', label: 'Dive Purpose', type: 'text', required: true, placeholder: 'e.g., Hull inspection' }
  ],
  checks: [
    { id: 'div_1', text: 'Dive plan reviewed and understood', required: true },
    { id: 'div_2', text: 'Vessel secured and propulsion stopped', required: true },
    { id: 'div_3', text: 'Alpha flag displayed', required: true },
    { id: 'div_4', text: 'Communications with dive team confirmed', required: true },
    { id: 'div_5', text: 'Emergency procedures agreed', required: true }
  ]
};

/**
 * Restricted Visibility - Simplified
 * Regulatory: COLREGs Rule 19
 */
export const RESTRICTED_VISIBILITY_CHECKLIST = {
  id: 'restricted_visibility',
  name: 'Restricted Visibility',
  description: 'When visibility reduced (fog, rain, etc.)',
  icon: 'Eye',
  regulatory: 'COLREGs Rule 19',
  isCustomisable: true,
  checks: [
    { id: 'rv_1', text: 'Radar on and operational', required: true },
    { id: 'rv_2', text: 'Safe speed for conditions', required: true },
    { id: 'rv_3', text: 'Fog signals being made', required: true },
    { id: 'rv_4', text: 'Navigation lights on', required: true },
    { id: 'rv_5', text: 'VHF Ch 16 monitored', required: true },
    { id: 'rv_6', text: 'Lookout posted', required: false },
    { id: 'rv_7', text: 'Engines ready for immediate manoeuvre', required: false }
  ]
};

/**
 * Map of operation types to their checklists
 */
export const OPERATION_CHECKLISTS = {
  [OPERATION_TYPES.CREW_TRANSFER]: CREW_TRANSFER_CHECKLIST,
  [OPERATION_TYPES.PILOT_TRANSFER]: PILOT_TRANSFER_CHECKLIST,
  [OPERATION_TYPES.TOWING]: TOWING_CHECKLIST,
  [OPERATION_TYPES.ANCHORING]: ANCHORING_CHECKLIST,
  [OPERATION_TYPES.STANDBY]: STANDBY_CHECKLIST,
  [OPERATION_TYPES.DIVING_SUPPORT]: DIVING_SUPPORT_CHECKLIST,
  [OPERATION_TYPES.RESTRICTED_VISIBILITY]: RESTRICTED_VISIBILITY_CHECKLIST
};

// ============================================================================
// PHASE 3: OPERATION TEMPLATE HELPERS
// ============================================================================

/**
 * Get operation template with fallback chain:
 * 1. Custom template from voyageSettings (if fields/checks defined)
 * 2. Default template from OPERATION_CHECKLISTS
 * 3. Empty template (for brand new custom operations)
 *
 * @param {string} operationId - The operation type ID
 * @param {object} voyageSettings - The voyage_settings object
 * @returns {object} Template with fields and checks arrays
 */
export function getOperationTemplate(operationId, voyageSettings) {
  // Find custom operation in settings
  const customOp = voyageSettings?.operationsList?.find(op => op.id === operationId);

  // Get default template (with null guard)
  const defaultTemplate = OPERATION_CHECKLISTS[operationId];

  // Build result with fallback chain
  // Note: Check for array length, not just existence - empty arrays should fall back to defaults
  const hasCustomFields = customOp?.fields && customOp.fields.length > 0;
  const hasCustomChecks = customOp?.checks && customOp.checks.length > 0;

  return {
    id: operationId,
    label: customOp?.label || defaultTemplate?.name || operationId,
    fields: hasCustomFields ? customOp.fields : (defaultTemplate?.fields ?? []),
    checks: hasCustomChecks ? customOp.checks : (defaultTemplate?.checks ?? [])
  };
}

/**
 * Default operation templates for Browse Templates feature
 * Built from OPERATION_CHECKLISTS with regulatory info
 *
 * NOTE: Uses null guards to prevent crash if any checklist is undefined
 */
export const DEFAULT_OPERATION_TEMPLATES = Object.entries(OPERATION_CHECKLISTS)
  .filter(([id, config]) => config != null) // Guard against undefined entries
  .map(([id, config]) => ({
    id,
    label: config?.name || id,
    description: config?.description || '',
    icon: config?.icon || 'FileText',
    regulatory: config?.regulatory || false,
    fields: config?.fields || [],
    checks: config?.checks || []
  }));

// ============================================================================
// OPERATION CANCELLATION REASONS
// ============================================================================

/**
 * Cancellation reason codes for operations
 * Required when cancelling an operation (audit trail)
 */
export const OPERATION_CANCEL_REASONS = {
  WEATHER: 'weather',
  SAFETY: 'safety',
  EQUIPMENT: 'equipment',
  CLIENT: 'client',
  PERSONNEL: 'personnel',
  EXTERNAL: 'external',
  OTHER: 'other'
};

export const OPERATION_CANCEL_REASON_LABELS = {
  [OPERATION_CANCEL_REASONS.WEATHER]: 'Weather deterioration',
  [OPERATION_CANCEL_REASONS.SAFETY]: 'Safety concern identified',
  [OPERATION_CANCEL_REASONS.EQUIPMENT]: 'Equipment issue/failure',
  [OPERATION_CANCEL_REASONS.CLIENT]: 'Client request',
  [OPERATION_CANCEL_REASONS.PERSONNEL]: 'Personnel issue',
  [OPERATION_CANCEL_REASONS.EXTERNAL]: 'External factors',
  [OPERATION_CANCEL_REASONS.OTHER]: 'Other (specify)'
};

/**
 * Operation status values
 */
export const OPERATION_STATUS = {
  PENDING: 'pending',
  ACTIVE: 'active',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

export const OPERATION_STATUS_LABELS = {
  [OPERATION_STATUS.PENDING]: 'Pending',
  [OPERATION_STATUS.ACTIVE]: 'In Progress',
  [OPERATION_STATUS.COMPLETED]: 'Completed',
  [OPERATION_STATUS.CANCELLED]: 'Cancelled'
};

// ============================================================================
// UNIFIED VOYAGE LOG V2 CONSTANTS
// ============================================================================

/**
 * Entry types for unified voyage log V2
 * This is the FLAT LOG structure - every entry in voyage.unifiedLog has one of these types
 */
export const UNIFIED_ENTRY_TYPES = {
  SECTION: 'section',                     // Operation header divider (=== CREW TRANSFER ===)
  OPERATION_DATA: 'operation_data',       // Operation details (Platform, Persons, etc.)
  CHECK: 'check',                         // Checkbox item
  MILESTONE: 'milestone',                 // LET GO or ALL FAST
  POSITION: 'position',                   // GPS position log
  WEATHER: 'weather',                     // Weather observation
  NOTE: 'note',                          // Free text note
  COMMS: 'comms',                        // Radio/communication log
  OPERATION_COMPLETE: 'operation_complete', // Marks operation done
  FUEL_READING: 'fuel_reading'            // Fuel level reading
};

// ============================================================================
// FUEL TRACKING
// ============================================================================

/**
 * Fuel level options for quick selection
 * Values represent percentage (0-100)
 */
export const FUEL_LEVELS = {
  FULL: { value: 100, label: 'Full', shortLabel: 'F' },
  THREE_QUARTERS: { value: 75, label: '3/4', shortLabel: '3/4' },
  HALF: { value: 50, label: '1/2', shortLabel: '1/2' },
  QUARTER: { value: 25, label: '1/4', shortLabel: '1/4' },
  RESERVE: { value: 10, label: 'Reserve', shortLabel: 'R' }
};

/**
 * Ordered array for UI rendering
 */
export const FUEL_LEVEL_OPTIONS = [
  FUEL_LEVELS.FULL,
  FUEL_LEVELS.THREE_QUARTERS,
  FUEL_LEVELS.HALF,
  FUEL_LEVELS.QUARTER,
  FUEL_LEVELS.RESERVE
];

/**
 * Milestone types for voyage log
 */
export const MILESTONE_TYPES = {
  LET_GO: 'let_go',
  ALL_FAST: 'all_fast',
  PRE_DEPARTURE_COMPLETE: 'pre_departure_complete'
};

/**
 * Check immutability window (5 minutes in milliseconds)
 * After this time, completed/skipped checks cannot be modified
 */
export const CHECK_IMMUTABILITY_WINDOW_MS = 5 * 60 * 1000;

/**
 * Legacy: Additional log entry types for unified log
 */
export const UNIFIED_LOG_ENTRY_TYPES = {
  OPERATION_CHECK: 'operation_check',
  OPERATION_EVENT: 'operation_event',
  OPERATION_ABORT: 'operation_abort'
};

/**
 * Reasons for aborting an operation
 */
export const ABORT_REASONS = {
  WEATHER: 'weather',
  SAFETY: 'safety',
  EQUIPMENT: 'equipment',
  CLIENT_REQUEST: 'client_request',
  OPERATIONAL: 'operational',
  OTHER: 'other'
};

export const ABORT_REASON_LABELS = {
  [ABORT_REASONS.WEATHER]: 'Weather deterioration',
  [ABORT_REASONS.SAFETY]: 'Safety concern',
  [ABORT_REASONS.EQUIPMENT]: 'Equipment failure',
  [ABORT_REASONS.CLIENT_REQUEST]: 'Client request',
  [ABORT_REASONS.OPERATIONAL]: 'Operational requirement',
  [ABORT_REASONS.OTHER]: 'Other (specify)'
};

/**
 * Reasons for skipping a check
 */
export const SKIP_REASONS = {
  NOT_APPLICABLE: 'not_applicable',
  WEATHER_CONDITIONS: 'weather_conditions',
  EQUIPMENT_ISSUE: 'equipment_issue',
  ALTERNATIVE_METHOD: 'alternative_method',
  CLIENT_WAIVER: 'client_waiver',
  OTHER: 'other'
};

export const SKIP_REASON_LABELS = {
  [SKIP_REASONS.NOT_APPLICABLE]: 'Not applicable to this operation',
  [SKIP_REASONS.WEATHER_CONDITIONS]: 'Weather conditions prevent',
  [SKIP_REASONS.EQUIPMENT_ISSUE]: 'Equipment unavailable',
  [SKIP_REASONS.ALTERNATIVE_METHOD]: 'Alternative method used',
  [SKIP_REASONS.CLIENT_WAIVER]: 'Client waiver obtained',
  [SKIP_REASONS.OTHER]: 'Other (specify)'
};

/**
 * Immutability window for check modifications (5 minutes in ms)
 */
export const CHECK_IMMUTABILITY_WINDOW = 5 * 60 * 1000;

/**
 * Voyage purpose options
 */
export const VOYAGE_PURPOSE = {
  CREW_TRANSFER: 'crew_transfer',
  SURVEY: 'survey',
  PASSENGER_RUN: 'passenger_run',
  OTHER: 'other'
};

export const VOYAGE_PURPOSE_LABELS = {
  [VOYAGE_PURPOSE.CREW_TRANSFER]: 'Crew Transfer',
  [VOYAGE_PURPOSE.SURVEY]: 'Survey',
  [VOYAGE_PURPOSE.PASSENGER_RUN]: 'Passenger Run',
  [VOYAGE_PURPOSE.OTHER]: 'Other'
};

// ============================================================================
// OPERATIONAL CHECKLISTS
// ============================================================================

/**
 * Checklist categories
 */
export const CHECKLIST_CATEGORIES = {
  SMS: 'sms',           // High-risk, permit style (Hot Work, Enclosed Space, Lifting)
  OPERATIONAL: 'operational',  // Standard checklists (Bunkering, Anchoring, Mooring)
  COMPANY: 'company'    // Custom company checklists
};

export const CHECKLIST_CATEGORY_LABELS = {
  [CHECKLIST_CATEGORIES.SMS]: 'SMS Checklists',
  [CHECKLIST_CATEGORIES.OPERATIONAL]: 'Operational Checklists',
  [CHECKLIST_CATEGORIES.COMPANY]: 'Company Checklists'
};

/**
 * Bunkering types
 */
export const BUNKERING_TYPES = {
  SIMPLE: 'simple',     // <100L, jerrycan, log entry only
  FULL: 'full'          // Ship-to-ship, barge, full checklist
};

export const BUNKERING_TYPE_LABELS = {
  [BUNKERING_TYPES.SIMPLE]: 'Simple Fuel Top-Up',
  [BUNKERING_TYPES.FULL]: 'Full Bunkering Operation'
};

/**
 * Fuel types for bunkering
 */
export const FUEL_TYPES = {
  DIESEL_MGO: 'diesel_mgo',
  PETROL: 'petrol'
};

export const FUEL_TYPE_LABELS = {
  [FUEL_TYPES.DIESEL_MGO]: 'Diesel (MGO)',
  [FUEL_TYPES.PETROL]: 'Petrol'
};

/**
 * Permit statuses
 */
export const PERMIT_STATUS = {
  ISSUED: 'issued',
  EXTENDED: 'extended',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled',
  EXPIRED: 'expired'
};

export const PERMIT_STATUS_LABELS = {
  [PERMIT_STATUS.ISSUED]: 'Issued',
  [PERMIT_STATUS.EXTENDED]: 'Extended',
  [PERMIT_STATUS.COMPLETED]: 'Completed',
  [PERMIT_STATUS.CANCELLED]: 'Cancelled',
  [PERMIT_STATUS.EXPIRED]: 'Expired'
};

/**
 * Permit types (SMS high-risk operations)
 */
export const PERMIT_TYPES = {
  HOT_WORK: 'hot_work',
  ENCLOSED_SPACE: 'enclosed_space',
  LIFTING: 'lifting'
};

export const PERMIT_TYPE_LABELS = {
  [PERMIT_TYPES.HOT_WORK]: 'Hot Work Permit',
  [PERMIT_TYPES.ENCLOSED_SPACE]: 'Enclosed Space Entry',
  [PERMIT_TYPES.LIFTING]: 'Lifting Operations'
};

// ============================================================================
// MAIN LOGBOOK
// ============================================================================

/**
 * Main logbook entry types (what appears in vessel logbook)
 */
export const LOGBOOK_ENTRY_TYPES = {
  // Existing types
  VOYAGE: 'voyage',           // Let Go / All Fast
  DRILL: 'drill',
  BUNKERING: 'bunkering',
  DEFECT: 'defect',
  EMERGENCY: 'emergency',
  CREW_CHANGE: 'crew_change',
  MAINTENANCE: 'maintenance',  // Critical jobs only
  MANUAL: 'manual',
  // New types (added 2025-01-10)
  INCIDENT: 'incident',              // Safety incidents, accidents, near misses
  FAMILIARISATION: 'familiarisation', // Crew familiarisation completion
  HOR_VIOLATION: 'hor_violation',    // Hours of Rest breaches
  CERTIFICATE: 'certificate',        // Certificate expiry/renewal
  SIGN_OFF: 'sign_off',              // Master sign-off entries
  PERMIT: 'permit',                  // Permit to work (hot work, enclosed space)
  DEFECT_RESOLVED: 'defect_resolved', // Distinct from raising defect
  CHECKLIST: 'checklist',            // Generic checklist completion
  DAILY_CHECK: 'daily_check',         // FT-87: Daily checks completion
  COMPLIANCE_OVERRIDE: 'compliance_override'  // FT-107: Pre-departure compliance override with reason
};

export const LOGBOOK_ENTRY_TYPE_LABELS = {
  [LOGBOOK_ENTRY_TYPES.VOYAGE]: 'Voyage',
  [LOGBOOK_ENTRY_TYPES.DRILL]: 'Drill',
  [LOGBOOK_ENTRY_TYPES.BUNKERING]: 'Bunkering',
  [LOGBOOK_ENTRY_TYPES.DEFECT]: 'Defect',
  [LOGBOOK_ENTRY_TYPES.EMERGENCY]: 'Emergency',
  [LOGBOOK_ENTRY_TYPES.CREW_CHANGE]: 'Crew Change',
  [LOGBOOK_ENTRY_TYPES.MAINTENANCE]: 'Maintenance',
  [LOGBOOK_ENTRY_TYPES.MANUAL]: 'Manual Entry',
  [LOGBOOK_ENTRY_TYPES.INCIDENT]: 'Incident',
  [LOGBOOK_ENTRY_TYPES.FAMILIARISATION]: 'Familiarisation',
  [LOGBOOK_ENTRY_TYPES.HOR_VIOLATION]: 'Hours Violation',
  [LOGBOOK_ENTRY_TYPES.CERTIFICATE]: 'Certificate',
  [LOGBOOK_ENTRY_TYPES.SIGN_OFF]: 'Sign-Off',
  [LOGBOOK_ENTRY_TYPES.PERMIT]: 'Permit',
  [LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED]: 'Defect Resolved',
  [LOGBOOK_ENTRY_TYPES.CHECKLIST]: 'Checklist Completed',
  [LOGBOOK_ENTRY_TYPES.DAILY_CHECK]: 'Daily Check',  // FT-87
  [LOGBOOK_ENTRY_TYPES.COMPLIANCE_OVERRIDE]: 'Compliance Override'  // FT-107
};

/**
 * Icons for main logbook entry types
 */
export const LOGBOOK_ENTRY_TYPE_ICONS = {
  [LOGBOOK_ENTRY_TYPES.VOYAGE]: 'Anchor',
  [LOGBOOK_ENTRY_TYPES.DRILL]: 'AlertTriangle',
  [LOGBOOK_ENTRY_TYPES.BUNKERING]: 'Fuel',
  [LOGBOOK_ENTRY_TYPES.DEFECT]: 'AlertCircle',
  [LOGBOOK_ENTRY_TYPES.EMERGENCY]: 'Siren',
  [LOGBOOK_ENTRY_TYPES.CREW_CHANGE]: 'Users',
  [LOGBOOK_ENTRY_TYPES.MAINTENANCE]: 'Wrench',
  [LOGBOOK_ENTRY_TYPES.MANUAL]: 'FileText',
  [LOGBOOK_ENTRY_TYPES.INCIDENT]: 'ShieldAlert',
  [LOGBOOK_ENTRY_TYPES.FAMILIARISATION]: 'UserCheck',
  [LOGBOOK_ENTRY_TYPES.HOR_VIOLATION]: 'Clock',
  [LOGBOOK_ENTRY_TYPES.CERTIFICATE]: 'Award',
  [LOGBOOK_ENTRY_TYPES.SIGN_OFF]: 'PenTool',
  [LOGBOOK_ENTRY_TYPES.PERMIT]: 'ClipboardCheck',
  [LOGBOOK_ENTRY_TYPES.DEFECT_RESOLVED]: 'CheckCircle',
  [LOGBOOK_ENTRY_TYPES.CHECKLIST]: 'ClipboardList',
  [LOGBOOK_ENTRY_TYPES.DAILY_CHECK]: 'CheckSquare',  // FT-87
  [LOGBOOK_ENTRY_TYPES.COMPLIANCE_OVERRIDE]: 'ShieldX'  // FT-107
};

/**
 * Manual entry categories for main logbook
 */
export const MANUAL_ENTRY_CATEGORIES = {
  GENERAL: 'general',
  VISITOR: 'visitor',
  PORT_STATE_INSPECTION: 'port_state_inspection',
  NOTICE_RECEIVED: 'notice_received',
  WEATHER_EVENT: 'weather_event',
  OTHER: 'other'
};

export const MANUAL_ENTRY_CATEGORY_LABELS = {
  [MANUAL_ENTRY_CATEGORIES.GENERAL]: 'General',
  [MANUAL_ENTRY_CATEGORIES.VISITOR]: 'Visitor on board',
  [MANUAL_ENTRY_CATEGORIES.PORT_STATE_INSPECTION]: 'Port State inspection',
  [MANUAL_ENTRY_CATEGORIES.NOTICE_RECEIVED]: 'Notice received',
  [MANUAL_ENTRY_CATEGORIES.WEATHER_EVENT]: 'Weather event',
  [MANUAL_ENTRY_CATEGORIES.OTHER]: 'Other'
};

// ============================================================================
// WEATHER OPTIONS
// ============================================================================

/**
 * Beaufort wind scale options (simplified for quick selection)
 */
export const BEAUFORT_SCALE = [
  { value: 'F0-F1', label: 'F0-F1 - Calm/Light Air' },
  { value: 'F2', label: 'F2 - Light Breeze' },
  { value: 'F3', label: 'F3 - Gentle Breeze' },
  { value: 'F4', label: 'F4 - Moderate Breeze' },
  { value: 'F5', label: 'F5 - Fresh Breeze' },
  { value: 'F6', label: 'F6 - Strong Breeze' },
  { value: 'F7', label: 'F7 - Near Gale' },
  { value: 'F8+', label: 'F8+ - Gale/Storm' }
];

/**
 * Legacy detailed wind force (kept for backward compatibility)
 */
export const WIND_FORCE = [
  { value: 0, label: 'F0 - Calm' },
  { value: 1, label: 'F1 - Light air' },
  { value: 2, label: 'F2 - Light breeze' },
  { value: 3, label: 'F3 - Gentle breeze' },
  { value: 4, label: 'F4 - Moderate breeze' },
  { value: 5, label: 'F5 - Fresh breeze' },
  { value: 6, label: 'F6 - Strong breeze' },
  { value: 7, label: 'F7 - Near gale' },
  { value: 8, label: 'F8 - Gale' },
  { value: 9, label: 'F9 - Strong gale' },
  { value: 10, label: 'F10 - Storm' },
  { value: 11, label: 'F11 - Violent storm' },
  { value: 12, label: 'F12 - Hurricane' }
];

/**
 * Wind direction options
 */
export const WIND_DIRECTIONS = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW', 'Variable'];

export const WIND_DIRECTION = [
  { value: 'N', label: 'N' },
  { value: 'NE', label: 'NE' },
  { value: 'E', label: 'E' },
  { value: 'SE', label: 'SE' },
  { value: 'S', label: 'S' },
  { value: 'SW', label: 'SW' },
  { value: 'W', label: 'W' },
  { value: 'NW', label: 'NW' },
  { value: 'Variable', label: 'Variable' }
];

/**
 * Sea state options (with wave heights)
 */
export const SEA_STATES = [
  { value: 'calm', label: 'Calm (0-0.1m)' },
  { value: 'smooth', label: 'Smooth (0.1-0.5m)' },
  { value: 'slight', label: 'Slight (0.5-1.25m)' },
  { value: 'moderate', label: 'Moderate (1.25-2.5m)' },
  { value: 'rough', label: 'Rough (2.5-4m)' },
  { value: 'very_rough', label: 'Very Rough (4-6m)' },
  { value: 'high', label: 'High (6m+)' }
];

/**
 * Legacy sea state (kept for backward compatibility)
 */
export const SEA_STATE = [
  { value: 'calm', label: 'Calm' },
  { value: 'slight', label: 'Slight' },
  { value: 'moderate', label: 'Moderate' },
  { value: 'rough', label: 'Rough' },
  { value: 'very_rough', label: 'Very Rough' },
  { value: 'high', label: 'High' }
];

/**
 * Visibility options (nautical)
 */
export const VISIBILITY_OPTIONS = [
  { value: 'fog', label: 'Fog (<0.5nm)' },
  { value: 'poor', label: 'Poor (0.5-2nm)' },
  { value: 'moderate', label: 'Moderate (2-5nm)' },
  { value: 'good', label: 'Good (>5nm)' }
];

/**
 * Legacy visibility (kept for backward compatibility)
 */
export const VISIBILITY = [
  { value: 'excellent', label: 'Excellent (>10nm)' },
  { value: 'good', label: 'Good (5-10nm)' },
  { value: 'moderate', label: 'Moderate (2-5nm)' },
  { value: 'poor', label: 'Poor (1-2nm)' },
  { value: 'very_poor', label: 'Very Poor (<1nm)' },
  { value: 'fog', label: 'Fog' }
];

/**
 * Met Office forecast URL
 */
export const MET_OFFICE_FORECAST_URL = 'https://www.metoffice.gov.uk/weather/specialist-forecasts/coast-and-sea/inshore-waters-forecast';

// ============================================================================
// PRE-DEPARTURE CHECKS (Default Template)
// ============================================================================

// Pre-departure checks moved to: src/config/operations/preDepartureChecksLibrary.js
// Use getVoyageStartChecks() from that file

// ============================================================================
// PRE-ARRIVAL CHECKS (BEFORE arriving - while still underway)
// ============================================================================

/**
 * Default pre-arrival check items
 * These checks are OPTIONAL and non-blocking
 */
export const DEFAULT_PRE_ARRIVAL_CHECKS = [
  { id: 'berth_confirmed', text: 'Berth/location confirmed', required: true, hasNotes: true },
  { id: 'tide_acceptable', text: 'Tide window acceptable', required: true, hasNotes: true },
  { id: 'mooring_ready', text: 'Mooring arrangements ready', required: true },
  { id: 'port_contacted', text: 'Port/terminal contacted', required: false },
  { id: 'hazards_reviewed', text: 'Approach hazards reviewed', required: false }
];

/**
 * Pre-arrival check templates for different arrival scenarios
 */
export const PRE_ARRIVAL_TEMPLATES = {
  default: {
    id: 'default',
    name: 'Standard Arrival',
    checks: DEFAULT_PRE_ARRIVAL_CHECKS
  },
  owf: {
    id: 'owf',
    name: 'Offshore Wind Farm',
    checks: [
      ...DEFAULT_PRE_ARRIVAL_CHECKS,
      { id: 'sov_notified', text: 'SOV/Platform notified', required: true },
      { id: 'transfer_method', text: 'Transfer method confirmed', required: true }
    ]
  },
  anchor: {
    id: 'anchor',
    name: 'Anchoring',
    checks: [
      { id: 'anchor_position', text: 'Anchor position selected', required: true, hasNotes: true },
      { id: 'depth_suitable', text: 'Depth suitable', required: true, hasNotes: true },
      { id: 'scope_planned', text: 'Scope/cable length planned', required: true, hasNotes: true },
      { id: 'anchor_watch', text: 'Anchor watch assigned', required: true }
    ]
  }
};

/**
 * Default arrival check items (POST-arrival reminders only, not blockers)
 * NOTE: Fuel is captured in PreArrivalPhase before All Fast, not here
 */
export const DEFAULT_ARRIVAL_CHECKS = [
  { id: 'vessel_secured', text: 'Vessel secured', required: false },
  { id: 'defects', text: 'Any defects to report?', required: false, linkTo: 'defects' },
  { id: 'hours_of_work', text: 'Hours of work updated', required: false }
];

// ============================================================================
// PERMANENT PHASE OPERATIONS
// ============================================================================

/**
 * Default pre-departure checks (inline to avoid circular import)
 * Source: preDepartureChecksLibrary.js DEFAULT_CHECKS
 */
export const DEFAULT_PRE_DEPARTURE_CHECKS = [
  { id: 'weather', text: 'Weather forecast assessed suitable for voyage', required: true },
  { id: 'nav_equipment', text: 'Navigation equipment operational', required: true },
  { id: 'steering_test', text: 'Steering tested', required: true },
  { id: 'comms_check', text: 'Communications checked', required: true },
  { id: 'crew_brief', text: 'Crew briefed on voyage plan', required: true },
  { id: 'lsa_ready', text: 'Life-saving equipment ready for use', required: true }
];

/**
 * Permanent phase operations - these appear at top of operationsList
 * and cannot be deleted (permanent: true). They have dedicated phase UIs
 * but their checklists are editable via OperationTemplateEditor.
 *
 * phaseType identifies which phase component handles this operation:
 * - 'pre_departure' → StartVoyageFullScreen / PreDeparturePhase
 * - 'pre_arrival' → PreArrivalPhase
 * - 'post_arrival' → ArrivalPhase
 */
export const DEFAULT_PHASE_OPERATIONS = [
  {
    id: 'pre_departure',
    label: 'Pre-Departure Checks',
    permanent: true,
    phaseType: 'pre_departure',
    fields: [],
    checks: DEFAULT_PRE_DEPARTURE_CHECKS
  },
  {
    id: 'pre_arrival',
    label: 'Pre-Arrival Checks',
    permanent: true,
    phaseType: 'pre_arrival',
    fields: [],
    checks: DEFAULT_PRE_ARRIVAL_CHECKS
  },
  {
    id: 'post_arrival',
    label: 'Post-Arrival Checks',
    permanent: true,
    phaseType: 'post_arrival',
    enabled: true,
    fields: [],
    checks: DEFAULT_ARRIVAL_CHECKS
  }
];

/**
 * Get checks for a specific phase operation from voyage_settings
 *
 * @param {string} phaseType - 'pre_departure', 'pre_arrival', or 'post_arrival'
 * @param {object} voyageSettings - The voyage_settings object from storage
 * @returns {Array} - Array of check objects, or defaults if not found
 */
export const getPhaseOperationChecks = (phaseType, voyageSettings) => {
  // Try to find phase operation in user's operationsList
  const phaseOp = voyageSettings?.operationsList?.find(op => op.phaseType === phaseType);

  if (phaseOp?.checks?.length > 0) {
    return phaseOp.checks;
  }

  // Fallback to defaults
  const defaultPhaseOp = DEFAULT_PHASE_OPERATIONS.find(op => op.phaseType === phaseType);
  return defaultPhaseOp?.checks || [];
};

/**
 * Check if a phase operation is enabled
 *
 * @param {string} phaseType - 'pre_departure', 'pre_arrival', or 'post_arrival'
 * @param {object} voyageSettings - The voyage_settings object from storage
 * @returns {boolean} - true if enabled (default true, only post_arrival can be disabled)
 */
export const isPhaseOperationEnabled = (phaseType, voyageSettings) => {
  const phaseOp = voyageSettings?.operationsList?.find(op => op.phaseType === phaseType);

  // If not found or no enabled property, default to true
  if (!phaseOp || phaseOp.enabled === undefined) {
    return true;
  }

  return phaseOp.enabled !== false;
};

// ============================================================================
// PERMIT TIME THRESHOLDS
// ============================================================================

/**
 * Time thresholds for permit warning states (in milliseconds)
 */
export const PERMIT_TIME_THRESHOLDS = {
  WARNING: 60 * 60 * 1000,    // 1 hour - amber warning
  CRITICAL: 15 * 60 * 1000   // 15 minutes - red critical
};

/**
 * Default permit validity (in hours)
 */
export const DEFAULT_PERMIT_VALIDITY_HOURS = 4;

// ============================================================================
// STORAGE KEYS
// ============================================================================

/**
 * Storage keys for operations module data
 * Note: Vessel scoping is handled by useStorageState hook
 */
export const OPERATIONS_STORAGE_KEYS = {
  VOYAGES: 'operations_voyages',
  CHECKLISTS: 'operations_checklists',
  CHECKLIST_TEMPLATES: 'operations_checklist_templates',
  PERMITS: 'operations_permits',
  MAIN_LOGBOOK: 'operations_main_logbook',
  BUNKERING_RECORDS: 'operations_bunkering',
  FAVOURITES: 'operations_checklist_favourites',
  VOYAGE_TEMPLATES: 'operations_voyage_templates'  // Phase 3: Voyage templates
};

// ============================================================================
// UI CONFIGURATION
// ============================================================================

/**
 * Touch target sizes (in pixels)
 */
export const TOUCH_TARGETS = {
  MINIMUM: 56,     // Minimum touch target height
  PRIMARY: 64      // Primary button height
};

/**
 * Tab configuration for OperationsModule
 */
export const OPERATIONS_TABS = [
  { id: 'voyage', label: 'Voyage', icon: 'Ship' },
  { id: 'checklists', label: 'Checklists', icon: 'ClipboardCheck' },
  { id: 'logbook', label: 'Logbook', icon: 'BookOpen' }
];

// ============================================================================
// EXPORT FORMATS
// ============================================================================

export const EXPORT_FORMATS = {
  PDF: 'pdf',
  CSV: 'csv',
  EMAIL: 'email'
};

export const EXPORT_FORMAT_LABELS = {
  [EXPORT_FORMATS.PDF]: 'PDF (formatted for print/MCA)',
  [EXPORT_FORMATS.CSV]: 'CSV (spreadsheet)',
  [EXPORT_FORMATS.EMAIL]: 'Email summary'
};

// ============================================================================
// COMMUNICATION TYPES
// ============================================================================

export const COMMS_TYPES = {
  VHF: 'vhf',
  SATELLITE: 'satellite',
  PHONE: 'phone'
};

export const COMMS_TYPE_LABELS = {
  [COMMS_TYPES.VHF]: 'VHF',
  [COMMS_TYPES.SATELLITE]: 'Satellite',
  [COMMS_TYPES.PHONE]: 'Phone'
};

// ============================================================================
// DRILL MODE
// ============================================================================

export const DRILL_MODES = {
  REAL: 'real',
  DRILL: 'drill'
};

export const DRILL_MODE_LABELS = {
  [DRILL_MODES.REAL]: 'Real Emergency',
  [DRILL_MODES.DRILL]: 'Drill / Practice'
};

// ============================================================================
// NAVIGATION CHECKLISTS
// ============================================================================
// Re-export from dedicated library file

export {
  NAVIGATION_CHECKLISTS,
  NAVIGATION_CHECKLIST_LIST,
  HEAVY_WEATHER_CHECKLIST as NAV_HEAVY_WEATHER_CHECKLIST,
  RESTRICTED_VISIBILITY_CHECKLIST as NAV_RESTRICTED_VISIBILITY_CHECKLIST,
  NIGHT_NAVIGATION_CHECKLIST,
  HARBOUR_ENTRY_CHECKLIST,
  NAV_ANCHORING_CHECKLIST,
  WATCH_HANDOVER_CHECKLIST
} from './operations/navigationChecklistsLibrary';
